"use client";
import { Link } from "react-router-dom";
import Section from "../components/Section";
import Card from "../components/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEarthAmericas } from "@fortawesome/free-solid-svg-icons/faEarthAmericas";
import { faBolt } from "@fortawesome/free-solid-svg-icons/faBolt";
import { faShieldHalved } from "@fortawesome/free-solid-svg-icons/faShieldHalved";
import { faLink } from "@fortawesome/free-solid-svg-icons/faLink";

const VALUES = [
  {
    title: "Uncompromising Reliability",
    description:
      "Our commitment to 99.99% uptime isn't just a promise—it's backed by redundant infrastructure, proactive monitoring, and decades of telecommunications expertise.",
    metric: "99.99%",
  },
  {
    title: "Continuous Innovation",
    description:
      "We invest heavily in R&D, emerging technologies, and strategic partnerships to ensure our clients always have access to the most advanced telecommunications solutions.",
    metric: "24/7",
  },
  {
    title: "Strategic Partnership",
    description:
      "We don't just provide services—we become an extension of your team, aligning to your goals and crafting communication strategies that drive growth.",
    metric: "Global",
  },
  {
    title: "Enterprise Security",
    description:
      "Standards-aligned, zero-trust design with encryption-in-depth and emerging quantum-safe protocols to protect critical communications.",
    metric: "Secure",
  },
];

const About = () => {
  return (
    <div className="animate-fade-in">
      <Section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 lg:pb-24 overflow-visible bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center max-w-6xl mx-auto space-y-6 sm:space-y-10 px-4 sm:px-6">
          <h1 className="break-words text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight leading-[1.15]">
            About
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mt-2 sm:mt-3 pb-[0.22em] -mb-[0.22em] [-webkit-text-fill-color:transparent]">
              Faef Global
            </span>
          </h1>
          <p className="text-base sm:text-lg md:text-2xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
            Pioneering the next generation of global telecommunications
            infrastructure with enterprise-grade solutions that connect
            businesses across continents.
          </p>
        </div>
      </Section>

      <Section className="py-24 sm:py-28 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-7xl mx-auto px-4 sm:px-6">
          <div className="space-y-6 sm:space-y-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-[1.15]">
              Our Mission
            </h2>
            <div className="space-y-4 sm:space-y-6">
              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
                To revolutionize global business communications by delivering
                the world's most advanced, reliable, and secure
                telecommunications infrastructure.
              </p>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
                Since our founding in 2010, we've been at the forefront of
                innovation—building networks that don't just connect, they
                transform how businesses operate in the digital economy.
              </p>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
                Our commitment extends beyond connectivity. We're building the
                intelligent infrastructure that will power the next decade of
                business innovation.
              </p>
            </div>
          </div>

          <div className="rounded-3xl p-10 sm:p-12 text-center text-white bg-gradient-to-br from-blue-600 to-indigo-600 shadow-xl">
            <div className="flex items-center justify-center mb-8">
              <FontAwesomeIcon
                icon={faEarthAmericas}
                className="text-white/90 text-6xl sm:text-7xl md:text-8xl"
                aria-hidden="true"
              />
            </div>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
              Global Infrastructure
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-blue-100 leading-relaxed">
              Spanning six continents with quantum-safe security, AI-powered
              optimization, and 24/7 intelligent monitoring.
            </p>
          </div>
        </div>
      </Section>

      <Section className="py-24 sm:py-28 lg:py-32">
        <div className="text-center mb-14 sm:mb-16 lg:mb-20 space-y-6 sm:space-y-8 px-4 sm:px-6">
          <h2 className="break-words text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight leading-[1.15]">
            Our Core
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mt-2 sm:mt-3 pb-[0.18em] -mb-[0.18em] [-webkit-text-fill-color:transparent]">
              Values
            </span>
          </h2>
          <p className="text-base sm:text-lg md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            The principles that drive every decision, every innovation, and
            every client relationship.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 max-w-7xl mx-auto px-4 sm:px-6">
          {VALUES.map((value) => (
            <Card
              key={value.title}
              className="h-full p-8 sm:p-10 space-y-4 sm:space-y-6 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-300 transition-all duration-300">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 break-words min-w-0">
                  {value.title}
                </h3>
                <div className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl font-bold text-sm sm:text-base md:text-lg text-blue-700 bg-blue-100 whitespace-nowrap leading-none flex-shrink-0">
                  <span className="tabular-nums">{value.metric}</span>
                </div>
              </div>

              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
                {value.description}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      <Section className="py-24 sm:py-28 lg:py-32">
        <div className="text-center mb-14 sm:mb-16 lg:mb-20 space-y-6 sm:space-y-8 px-4 sm:px-6">
          <h2 className="break-words text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight leading-[1.15]">
            Enterprise
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mt-2 sm:mt-3 pb-[0.18em] -mb-[0.18em] [-webkit-text-fill-color:transparent]">
              Excellence
            </span>
          </h2>
          <p className="text-base sm:text-lg md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            World-class telecommunications with unmatched reliability, security,
            and performance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 lg:gap-12 max-w-7xl mx-auto px-4 sm:px-6">
          <Card className="text-center p-8 sm:p-10 space-y-6 sm:space-y-8 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-300 transition-all duration-300">
            <div className="rounded-2xl w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto bg-gradient-to-br from-blue-100 to-indigo-100">
              <FontAwesomeIcon
                icon={faBolt}
                className="text-blue-700 text-3xl sm:text-4xl"
                aria-hidden="true"
              />
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              Ultra Performance
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
              Sub-5ms latency with 99.99% uptime across our global network.
            </p>
          </Card>

          <Card className="text-center p-8 sm:p-10 space-y-6 sm:space-y-8 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-300 transition-all duration-300">
            <div className="rounded-2xl w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto bg-gradient-to-br from-blue-100 to-indigo-100">
              <FontAwesomeIcon
                icon={faShieldHalved}
                className="text-blue-700 text-3xl sm:text-4xl"
                aria-hidden="true"
              />
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              Quantum Security
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
              Enterprise-grade encryption with quantum-safe protocols and
              zero-trust architecture.
            </p>
          </Card>

          <Card className="text-center p-8 sm:p-10 space-y-6 sm:space-y-8 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-300 transition-all duration-300">
            <div className="rounded-2xl w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto bg-gradient-to-br from-blue-100 to-indigo-100">
              <FontAwesomeIcon
                icon={faLink}
                className="text-blue-700 text-3xl sm:text-4xl"
                aria-hidden="true"
              />
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              Strategic Partnership
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
              Dedicated enterprise support with proactive monitoring and
              strategic consulting.
            </p>
          </Card>
        </div>
      </Section>

      {/* FINAL SECTION — simple & clean, uses router Link */}
      <Section className="py-28 lg:py-32 relative overflow-visible bg-gradient-to-br from-blue-700 via-indigo-700 to-indigo-800 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="break-words text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.12]">
            Built for Reliability
            <span className="block mt-3 text-blue-200">
              Telecom-grade at every layer
            </span>
          </h2>

          <p className="mt-6 text-base sm:text-lg md:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
            Simple architecture, predictable performance, and support that never
            sleeps.
          </p>
          <div className="mt-8">
            <Link
              to="/contact"
              className="inline-block text-lg md:text-xl px-8 sm:px-12 py-4 sm:py-5 rounded-2xl
                         bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-600
                         text-white shadow-2xl shadow-blue-900/30 ring-1 ring-white/10 focus:outline-none
                         focus:ring-2 focus:ring-white/20 transition-all duration-300">
              Contact us
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default About;
